/**
 * Локализация контента базы знаний: RU (источник — articles.ts) + EN-оверлей (articles.en.ts).
 * Где EN-перевода нет — аккуратный fallback на RU (без битых страниц).
 */
import type { KbArticle, KbCategory } from "./articles";
import { KB_ARTICLES_EN, KB_CATEGORIES_EN } from "./articles.en";

type Loc = "ru" | "en";

/** Статья на нужном языке: для 'en' подставляем EN-поля, где они есть; иначе RU. */
export function localizeArticle(a: KbArticle, locale: Loc): KbArticle {
  if (locale !== "en") return a;
  const en = KB_ARTICLES_EN[a.slug];
  if (!en) return a;
  return {
    ...a,
    title: en.title || a.title,
    intro: en.intro ?? a.intro,
    sections: en.sections && en.sections.length ? en.sections : a.sections,
    faq: en.faq ?? a.faq,
    images: en.images ?? a.images,
  };
}

export function localizeArticleOpt(a: KbArticle | undefined, locale: Loc): KbArticle | undefined {
  return a ? localizeArticle(a, locale) : a;
}

export function localizeCategory(c: KbCategory, locale: Loc): KbCategory {
  if (locale !== "en") return c;
  const en = KB_CATEGORIES_EN[c.id];
  if (!en) return c;
  return { ...c, title: en.title || c.title, description: en.description || c.description };
}

export function localizeCategoryOpt(c: KbCategory | undefined, locale: Loc): KbCategory | undefined {
  return c ? localizeCategory(c, locale) : c;
}

/** UI-строки самой базы знаний (не контент статей). */
export const KB_UI = {
  title: { ru: "База знаний", en: "Knowledge Base" },
  subtitle: {
    ru: "Инструкции по работе с системой — по разделам и с поиском.",
    en: "Guides for working with the system — by section and searchable.",
  },
  searchPlaceholder: {
    ru: "Поиск по базе знаний — например, «лимиты», «1С», «согласование»",
    en: "Search the knowledge base — e.g. “limits”, “1C”, “approval”",
  },
  found: { ru: "Найдено статей:", en: "Articles found:" },
  nothingFound: {
    ru: "Ничего не найдено. Попробуйте другой запрос или выберите раздел ниже.",
    en: "Nothing found. Try another query or pick a section below.",
  },
  popular: { ru: "Популярные статьи", en: "Popular articles" },
  inThisArticle: { ru: "В этой статье", en: "In this article" },
  faq: { ru: "Частые вопросы", en: "FAQ" },
  readingMin: { ru: "мин чтения", en: "min read" },
  updated: { ru: "Обновлено:", en: "Updated:" },
  helpful: { ru: "Статья помогла?", en: "Was this helpful?" },
  yes: { ru: "Да", en: "Yes" },
  no: { ru: "Нет", en: "No" },
  thanks: { ru: "Спасибо за отзыв!", en: "Thanks for your feedback!" },
  moreInCategory: { ru: "Ещё в разделе", en: "More in" },
  allKb: { ru: "Вся база знаний", en: "All articles" },
  backToKb: { ru: "Вернуться в базу знаний", en: "Back to knowledge base" },
  notFound: { ru: "Статья не найдена.", en: "Article not found." },
  breadcrumb: { ru: "База знаний", en: "Knowledge Base" },
} as const;

export function ui(key: keyof typeof KB_UI, locale: Loc): string {
  return KB_UI[key][locale === "en" ? "en" : "ru"];
}

/** Склонение «статья/статьи/статей» (RU) или "article/articles" (EN). */
export function pluralArticlesL(n: number, locale: Loc): string {
  if (locale === "en") return n === 1 ? "article" : "articles";
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "статья";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "статьи";
  return "статей";
}
